import React from 'react';
import { Helmet } from 'react-helmet';

export const SEOHead = ({
  title = 'Best Career Counselling in Bangalore, Jamshedpur & Ranchi | Free First Session | Nirvana',
  description = 'Expert career counselling in Bangalore, Jamshedpur & Ranchi. Get personalized career guidance from certified counsellor with 30+ years experience. Free first session, psychometric assessment.',
  keywords = 'career counselling bangalore, career counsellor jharkhand, career guidance bangalore, career counselling jamshedpur, best career counsellor jharkhand, career guidance ranchi, psychometric test bangalore, career assessment jharkhand, education counselling bangalore, career counselling for students bangalore jharkhand',
  canonicalUrl = 'https://nirvanacareercounselling.com',
  city = null,
  ogImage = 'https://nirvanacareercounselling.com/logo512.png',
}) => {
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'ProfessionalService'],
    name: 'Nirvana Career Counselling',
    url: 'https://nirvanacareercounselling.com',
    telephone: '+919731166822',
    email: 'sudutta@gmail.com',
    image: 'https://www.edumilestones.com/crm/images/logo.png',
    priceRange: '₹₹',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Jamshedpur',
      addressRegion: 'Jharkhand',
      addressCountry: 'IN',
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Bangalore',
        containedInPlace: { '@type': 'State', name: 'Karnataka' },
      },
      {
        '@type': 'City',
        name: 'Jamshedpur',
        containedInPlace: { '@type': 'State', name: 'Jharkhand' },
      },
      {
        '@type': 'City',
        name: 'Ranchi',
        containedInPlace: { '@type': 'State', name: 'Jharkhand' },
      },
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+919731166822',
      contactType: 'customer service',
      areaServed: ['IN-KA', 'IN-JH'],
      availableLanguage: ['English', 'Hindi'],
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '10:00',
        closes: '20:00',
      },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '50',
      bestRating: '5',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Do you provide career counselling services in Bangalore, Jamshedpur, and Ranchi?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! We provide comprehensive career counselling services for students and professionals in Bangalore, Jamshedpur, and Ranchi. Our sessions are available 100% online, ensuring you receive expert guidance regardless of your location.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does online career counselling work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our online career counselling sessions use video conferencing for face-to-face interaction. We share digital assessment reports in real-time and provide personalized career roadmaps. Clients from Jamshedpur, Ranchi, and Bangalore receive the same high-quality service from the comfort of their home.',
        },
      },
      {
        '@type': 'Question',
        name: 'Why is career counselling essential in today\'s world?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'With over 3000+ career options available, making an informed decision can be overwhelming. Professional career counselling helps you understand your strengths, interests, and personality traits, matching them with suitable career paths aligned with market demands.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does the career counselling process work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our process begins with a FREE initial consultation to understand your needs. We then conduct comprehensive psychometric assessments to evaluate your interests, aptitudes, and personality traits. Based on these results, we provide detailed career mapping and create a personalized roadmap for your career development.',
        },
      },
    ],
  };

  const citySchema = city
    ? {
        '@context': 'https://schema.org',
        '@type': ['LocalBusiness', 'ProfessionalService'],
        '@id': `https://nirvanacareercounselling.com/${city.toLowerCase()}`,
        name: `Nirvana Career Counselling — ${city}`,
        url: canonicalUrl,
        telephone: '+919731166822',
        email: 'sudutta@gmail.com',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Jamshedpur',
          addressRegion: 'Jharkhand',
          addressCountry: 'IN',
        },
        areaServed: {
          '@type': 'City',
          name: city,
        },
        description: description,
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '50',
          bestRating: '5',
        },
      }
    : null;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />

      <link rel="canonical" href={canonicalUrl} />

      {/* Geographic meta tags */}
      <meta name="geo.region" content="IN-KA;IN-JH" />
      <meta name="geo.placename" content="Bangalore;Jamshedpur;Ranchi" />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* LocalBusiness schema */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>

      {/* FAQ schema */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      {/* City-specific schema */}
      {citySchema && (
        <script type="application/ld+json">
          {JSON.stringify(citySchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
